export interface AffectedService {
    serviceType: string;
    serviceId: number;
    subscriptionId: string;
    tenantId: number;
    serviceName: string;
    customerName: string | null;
    chargebeeCustomerId: string | null;
    tags: string[];
}

export interface NetworkEventDetail {
    neighbor?: string;
    vrf?: string;
    afiSafi?: string;
    asn?: number;
    area?: string;
    peer?: string;
    level?: string;
}

export type EventStatus =
    | 'Down'
    | 'Resolved'
    | 'Up'
    | 'Warning';

export type EventBadge =
    // Protocol events
    | 'recovery'
    | 'outage'
    | 'link-restored'
    | 'link-lost'
    | 'adjacency-up'
    | 'adjacency-lost'
    // Interface events
    | 'port-up'
    | 'port-down'
    | 'admin-down'
    | 'zero-traffic'
    | 'traffic-resumed';

export interface NetworkEventRow {
    id: string;
    timestamp: string;
    eventType: string;
    severity: number;
    status: EventStatus;
    badge: EventBadge | null;
    reason: string | null;
    resolvedAt: string | null;
    resolvedByEventId: string | null;
    correlatedEventId: string | null;
    incidentId: string;
    note: string | null;
    affectedServices: AffectedService[];
    detail: NetworkEventDetail;
}

export interface IncidentGroup {
    incidentId: string;
    startedAt: string;
    resolvedAt: string | null;
    status: EventStatus;
    severity: number;
    eventTypes: string[];
    eventCount: number;
    affectedServices: AffectedService[];
    events: NetworkEventRow[];
}

export interface PaginationInfo {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
}

/**
 * Incident-side totals, present only on the incident-backed active
 * listing (`isActive=true` with the backend's KE_READ_FROM_INCIDENTS on).
 * `active` is the very number the side-menu badge renders, so surfacing
 * it here is what keeps the tab header and the badge from disagreeing.
 */
export interface NetworkEventsIncidentTotals {
    active: number;
}

export interface NetworkEventsResponse {
    events: NetworkEventRow[];
    pagination: PaginationInfo;
    /** Absent on the legacy event-scan path (resolved/unfiltered listings). */
    incidents?: NetworkEventsIncidentTotals;
    /** `'incidents_table'` when the listing came from the incident-backed path. */
    source?: string;
}

export const EVENT_TYPE_LABELS: Record<string, string> = {
    bgp_adjchange: 'BGP',
    bfd_session: 'BFD',
    interface_state_change: 'Interface State',
    zero_traffic: 'Data Rate',
    traffic_resumed: 'Recovered',
    // Both router_* events render as "Outage" so the History tab's Type
    // column dedupes them into a single tag per incident (an unreachable +
    // its recovery describes one outage from the operator's perspective).
    router_unreachable: 'Outage',
    router_reachable: 'Outage',
};

export const SERVICE_TYPE_LABELS: Record<string, string> = {
    KontrolEdgeInternetEnterprise: 'Internet Connectivity',
    internet_enterprise: 'Internet Connectivity',
    KontrolEdgeBBConnector: 'Backbone',
    bb_connector: 'Backbone',
    KontrolEdgeDataCenterInterconnect: 'DCI',
    dci: 'DCI',
    KontrolEdgeCloudConnectivity: 'Cloud Connectivity',
    cloud_connectivity: 'Cloud Connectivity',
    KontrolEdgePort: 'Port',
    port: 'Port',
};

/**
 * Maps a backend `serviceType` value to the route that renders the detail
 * page for that service. Returns null if the service type is not routable
 * (e.g. unsupported product or legacy service).
 */
export function getServiceDetailPath(
    serviceType: string,
    serviceId: number | string
): string | null {
    const id = encodeURIComponent(String(serviceId));
    switch (serviceType) {
        case 'KontrolEdgeInternetEnterprise':
        case 'internet_enterprise':
            return `/kontrol/internet/internet-details?connectionId=${id}`;
        case 'KontrolEdgeDataCenterInterconnect':
        case 'dci':
            return `/kontrol/datacenter-details?datacenterId=${id}`;
        case 'KontrolEdgeCloudConnectivity':
        case 'cloud_connectivity':
            return `/kontrol/cloud-connectivity/details?connectionId=${id}`;
        case 'KontrolEdgeBBConnector':
        case 'bb_connector':
            return `/kontrol/backbone-details?backboneId=${id}`;
        case 'KontrolEdgePort':
        case 'port':
            return `/kontrol/port-details?portId=${id}`;
        default:
            return null;
    }
}

export const BADGE_LABELS: Record<EventBadge, string> = {
    recovery: 'Recovery',
    outage: 'Outage',
    'link-restored': 'Link Restored',
    'link-lost': 'Link Lost',
    'adjacency-up': 'Adjacency Up',
    'adjacency-lost': 'Adjacency Lost',
    'port-up': 'Port Up',
    'port-down': 'Port Down',
    'admin-down': 'Admin Down',
    'zero-traffic': 'Idle',
    'traffic-resumed': 'Recovered',
};

export const BADGE_VARIANT: Record<EventBadge, 'success' | 'error' | 'warning'> = {
    recovery: 'success',
    outage: 'error',
    'link-restored': 'success',
    'link-lost': 'error',
    'adjacency-up': 'success',
    'adjacency-lost': 'error',
    'port-up': 'success',
    'port-down': 'error',
    'admin-down': 'error',
    'zero-traffic': 'warning',
    'traffic-resumed': 'success',
};

/**
 * "Trigger Event" column: maps a badge to the simplified up/down/warning
 * indicator we show in the Incident Details table. Single source of truth
 * so we don't drift between the badge taxonomy and what the column renders.
 */
export const BADGE_TRIGGER_STATE: Record<
    EventBadge,
    { label: 'Up' | 'Down' | 'Warning'; variant: 'success' | 'error' | 'warning' }
> = {
    recovery: { label: 'Up', variant: 'success' },
    'link-restored': { label: 'Up', variant: 'success' },
    'adjacency-up': { label: 'Up', variant: 'success' },
    'port-up': { label: 'Up', variant: 'success' },
    'traffic-resumed': { label: 'Up', variant: 'success' },
    outage: { label: 'Down', variant: 'error' },
    'link-lost': { label: 'Down', variant: 'error' },
    'adjacency-lost': { label: 'Down', variant: 'error' },
    'port-down': { label: 'Down', variant: 'error' },
    'admin-down': { label: 'Down', variant: 'error' },
    'zero-traffic': { label: 'Warning', variant: 'warning' },
};

export const SEVERITY_LABELS: Record<number, string> = {
    0: 'Emergency',
    1: 'Alert',
    2: 'Critical',
    3: 'Error',
    4: 'Warning',
    5: 'Notice',
    6: 'Info',
    7: 'Debug',
};

export function getEventTypeLabel(eventType: string): string {
    return EVENT_TYPE_LABELS[eventType] ?? eventType;
}

export function getServiceTypeLabel(serviceType: string): string {
    return SERVICE_TYPE_LABELS[serviceType] ?? serviceType;
}

export function getSeverityLabel(severity: number): string {
    return SEVERITY_LABELS[severity] ?? `Sev ${severity}`;
}

export function getSeverityVariant(
    severity: number
): 'error' | 'warning' | 'neutral' {
    if (severity <= 3) return 'error';
    if (severity <= 5) return 'warning';
    return 'neutral';
}

// 24-hour clock, 00–23. Without it the `en-US` locale adds AM/PM.
const TIME_24H: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
};

// dd/mm/yyyy HH:mm:ss, day first like the rest of the dashboard
// (`formatDate` in kontrolEdge/helpers, the Reflector V2 formatters).
export function formatTimestamp(iso: string): string {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy} ${formatTimeOnly(iso)}`;
}

export function formatTimeOnly(iso: string): string {
    return new Date(iso).toLocaleTimeString('en-US', TIME_24H);
}

export function formatDuration(ms: number): string {
    if (ms < 60_000) return '< 1m';
    const totalMinutes = Math.floor(ms / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
}

function deriveIncidentStatus(events: NetworkEventRow[]): EventStatus {
    if (events.some((e) => e.status === 'Down')) return 'Down';
    if (events.some((e) => e.status === 'Warning')) return 'Warning';
    if (events.every((e) => e.status === 'Up')) return 'Up';
    return 'Resolved';
}

function deduplicateServices(
    events: NetworkEventRow[]
): AffectedService[] {
    const seen = new Set<string>();
    const result: AffectedService[] = [];
    for (const evt of events) {
        for (const svc of evt.affectedServices) {
            const key = svc.serviceType + ':' + String(svc.serviceId);
            if (!seen.has(key)) {
                seen.add(key);
                result.push(svc);
            }
        }
    }
    return result;
}

export function groupEventsIntoIncidents(
    events: NetworkEventRow[]
): IncidentGroup[] {
    const groups = new Map<string, NetworkEventRow[]>();
    for (const evt of events) {
        const key = evt.incidentId;
        if (!groups.has(key)) groups.set(key, []);
        const group = groups.get(key);
        if (group) group.push(evt);
    }

    const incidents: IncidentGroup[] = [];
    for (const [incidentId, members] of Array.from(groups.entries())) {
        const sorted = [...members].sort(
            (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime()
        );

        const last = sorted.at(-1);
        const startedAt = last ? last.timestamp : sorted[0].timestamp;

        const resolvedTimestamps = sorted
            .map((e) => e.resolvedAt)
            .filter(Boolean) as string[];
        const hasActiveEvent = sorted.some(
            (e) => e.status === 'Down'
        );
        let resolvedAt: string | null = null;
        if (!hasActiveEvent && resolvedTimestamps.length > 0) {
            const sortedTs = [...resolvedTimestamps].sort(
                (a, b) => a.localeCompare(b)
            );
            resolvedAt = sortedTs.at(-1) ?? null;
        }

        const severity = Math.min(...sorted.map((e) => e.severity));
        const eventTypes = Array.from(
            new Set(sorted.map((e) => e.eventType))
        );

        incidents.push({
            incidentId,
            startedAt,
            resolvedAt,
            status: deriveIncidentStatus(sorted),
            severity,
            eventTypes,
            eventCount: sorted.length,
            affectedServices: deduplicateServices(sorted),
            events: sorted,
        });
    }

    incidents.sort(
        (a, b) =>
            new Date(b.startedAt).getTime() -
            new Date(a.startedAt).getTime()
    );

    return incidents;
}
